import type { ReactNode } from 'react';
import { SOURCE_LABEL, type SourceKind } from './types';
import { useInsurance } from './InsuranceContext';

/** A source marker chip (handoff §5). Always carries text; opens the fact
 *  popover, or the call / portal drawer when it points at a single event. */
export function SourceMarker({
  kind,
  factId,
  factTitle,
  factSources,
}: {
  kind: SourceKind;
  /** When set, the marker opens this fact's popover. */
  factId?: string | undefined;
  factTitle?: string | undefined;
  factSources?: SourceKind[] | undefined;
}) {
  const { openFact, openCall, openPortal } = useInsurance();
  const onClick = () => {
    if (kind === 'ai_call' && !factId) { openCall(); return; }
    if (kind === 'payer_portal' && !factId) { openPortal(); return; }
    openFact({ id: factId ?? `${kind}:${factTitle ?? ''}`, title: factTitle, sources: factSources });
  };
  return (
    <button type="button" className="ins-src" onClick={onClick} aria-label={`${factTitle ? `${factTitle}: ` : ''}${SOURCE_LABEL[kind]} — where this came from`}>
      {SOURCE_LABEL[kind]}
    </button>
  );
}

/** A row of source markers that all open the same fact. */
export function SourceMarkers({
  sources,
  factId,
  factTitle,
}: {
  sources: SourceKind[];
  factId?: string | undefined;
  factTitle?: string | undefined;
}) {
  return (
    <span className="ins-srcs">
      {sources.map((s) => (
        <SourceMarker key={s} kind={s} factId={factId} factTitle={factTitle} factSources={sources} />
      ))}
    </span>
  );
}

export function SectionHead({ id, title, sub, right }: { id?: string; title: string; sub?: ReactNode; right?: ReactNode }) {
  return (
    <div className="ins-section-head">
      <h2 className="ins-h2" id={id}>{title}</h2>
      {sub ? <span className="ins-section-sub">{sub}</span> : null}
      {right ? <span className="ins-section-sub right">{right}</span> : null}
    </div>
  );
}
